/**
 * Caption export (download) helpers. Core proxies the captions worker:
 *
 *   GET /captions/:id/sessions?limit=20
 *       -> { sessions: [{ id, startedAt, endedAt, expiresAt, endedReason, langs, durationSec, elapsedSec }] }
 *   GET /captions/:id/export
 *       -> { streamKey, languages, configured, formats, maxSpanSec }
 *   GET /captions/:id/export/<lang>.<ext>?from=<ISO>&to=<ISO>&t0=<ISO>
 *       -> file; headers Content-Disposition, X-Captions-Export-Segments, X-Captions-Export-Cues
 *          400 captions_export_invalid_range, 404 captions_export_not_found, 413 captions_export_too_large,
 *          504 captions_export_pending (SyncWords is still preparing the file: retry)
 *
 * A session downloads from its start to its end (or its expiry); a running
 * one up to now, as a snapshot. One file covers at most `maxSpanSec` (6 h):
 * longer sessions are offered in parts that share the session's `t0`, so
 * every part's timestamps count from the session start. No stitching.
 */
import { toMs } from './captionLanguages'

export const EXPORT_FORMATS = Object.freeze(['vtt', 'srt', 'json'])
// Every format a flow may offer: SyncWords flows answer vtt/srt/txt.
export const KNOWN_FORMATS = Object.freeze([...EXPORT_FORMATS, 'txt'])
export const DEFAULT_FORMAT = 'vtt'
export const MAX_SPAN_SEC = 21_600
export const SNAPSHOT_NOTICE = 'Snapshot so far — captions are still running'
export const NO_CAPTIONS = 'No captions found for this session'
// `captions_export_not_found`: the worker has no record of the stream yet (it never played).
export const NOTHING_RECORDED = 'No captions recorded for this stream yet.'
export const EXPORT_PENDING = 'These captions are still being prepared. Try again in a few seconds.'

/** A 504 `captions_export_pending`: the same download succeeds once the file is ready. */
export const isExportPending = (err) => err?.code === 'captions_export_pending' || err?.status === 504

/**
 * The languages a session can be downloaded in: the session's languages the
 * worker has (`exportLanguages`), or all of the session's when the worker
 * list is empty.
 */
export function sessionExportLangs(session, exportLanguages = []) {
	const own = Array.isArray(session?.langs) ? session.langs : []
	const worker = Array.isArray(exportLanguages) ? exportLanguages : []
	if (!own.length) return [...worker]
	if (!worker.length) return [...own]
	return own.filter((l) => worker.some((w) => String(w).toLowerCase() === String(l).toLowerCase()))
}

/** `{ streamKey, languages, configured, formats, maxSpanSec }` with safe defaults. */
export function normalizeExportInfo(raw) {
	const formats = (Array.isArray(raw?.formats) ? raw.formats : [])
		.map((f) => String(f).toLowerCase())
		.filter((f) => KNOWN_FORMATS.includes(f))
	const max = Number(raw?.maxSpanSec)
	return {
		streamKey: typeof raw?.streamKey === 'string' ? raw.streamKey : '',
		languages: Array.isArray(raw?.languages) ? raw.languages.filter((l) => typeof l === 'string' && l) : [],
		configured: raw?.configured !== false,
		formats: formats.length ? [...new Set(formats)] : [...EXPORT_FORMATS],
		maxSpanSec: Number.isFinite(max) && max > 0 ? max : MAX_SPAN_SEC,
	}
}

/** One session with epoch-ms times; null when it has no usable start. */
export function normalizeSession(raw) {
	const startedAt = toMs(raw?.startedAt)
	if (!Number.isFinite(startedAt)) return null
	const endedAt = toMs(raw?.endedAt)
	const expiresAt = toMs(raw?.expiresAt)
	return {
		id: String(raw?.id ?? startedAt),
		startedAt,
		endedAt: Number.isFinite(endedAt) ? endedAt : null,
		expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
		endedReason: raw?.endedReason || null,
		langs: Array.isArray(raw?.langs) ? raw.langs.filter((l) => typeof l === 'string' && l) : [],
		durationSec: Number.isFinite(Number(raw?.durationSec)) && raw?.durationSec !== null ? Number(raw.durationSec) : null,
		elapsedSec: Number.isFinite(Number(raw?.elapsedSec)) && raw?.elapsedSec !== null ? Number(raw.elapsedSec) : null,
	}
}

/** Still open: no end yet and not past its expiry (unlimited sessions have none). */
export function isRunningSession(session, now = Date.now()) {
	if (!session || session.endedAt !== null) return false
	return session.expiresAt === null || session.expiresAt > now
}

/** `{ from, to, running }` in epoch ms: the session's span, or up to now while it runs. */
export function sessionRange(session, now = Date.now()) {
	const running = isRunningSession(session, now)
	const to = running ? now : (session.endedAt ?? session.expiresAt ?? now)
	return { from: session.startedAt, to: Math.max(session.startedAt, to), running }
}

/** "Oct 9, 03:23 PM" in the viewer's locale. */
export function formatSessionStart(ms) {
	try {
		return new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
	} catch {
		return new Date(ms).toISOString()
	}
}

/** Seconds the session has captioned so far (or in total once it ended). */
export function sessionLengthSec(session, now = Date.now()) {
	const { from, to } = sessionRange(session, now)
	return Math.max(0, Math.round((to - from) / 1000))
}

/** "00:00", "06:00", "13:30": hours and minutes from `t0`. */
function offsetLabel(ms) {
	const totalMin = Math.floor(Math.max(0, ms) / 60_000)
	return `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`
}

/**
 * The downloads for one session: a single part, or one per `maxSpanSec`
 * chunk with the same `t0` and a "Part 1 of 3 (00:00–06:00)" label.
 */
export function exportParts(session, { maxSpanSec = MAX_SPAN_SEC, now = Date.now() } = {}) {
	const { from, to, running } = sessionRange(session, now)
	const spanMs = Math.max(1, maxSpanSec) * 1000
	const count = Math.max(1, Math.ceil((to - from) / spanMs))
	const parts = []
	for (let i = 0; i < count; i += 1) {
		const partFrom = from + i * spanMs
		const partTo = Math.min(to, partFrom + spanMs)
		parts.push({
			index: i,
			from: partFrom,
			to: partTo,
			t0: from,
			label: count > 1 ? `Part ${i + 1} of ${count} (${offsetLabel(partFrom - from)}–${offsetLabel(partTo - from)})` : '',
		})
	}
	return { parts, running }
}

/** The file name from a Content-Disposition header, or ''. */
export function filenameFromDisposition(header) {
	if (!header || typeof header !== 'string') return ''
	const star = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(header)
	if (star) {
		try {
			return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
		} catch {
			// fall through to the plain filename
		}
	}
	const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i.exec(header)
	return plain ? (plain[1] ?? plain[2] ?? '').trim() : ''
}

/** `<key>-<lang>-<fromISO>.<ext>` with the ISO time made file-name safe. */
export function fallbackFilename(streamKey, lang, fromMs, ext) {
	const iso = new Date(fromMs).toISOString().replace(/\.\d{3}Z$/, 'Z').replace(/:/g, '-')
	return `${streamKey || 'captions'}-${lang}-${iso}.${ext}`
}

/** Publisher copy for a failed download. */
export function exportErrorMessage(err) {
	switch (err?.code) {
		case 'captions_export_too_large':
			return 'Too many captions for one file — try a shorter range'
		case 'captions_export_not_found':
			return NOTHING_RECORDED
		case 'captions_export_invalid_range':
			return "This session's time range can't be exported."
		case 'captions_export_pending':
			return EXPORT_PENDING
		default:
			break
	}
	if (err?.status === 504) return EXPORT_PENDING
	if (err?.status === 413) return 'Too many captions for one file — try a shorter range'
	if (err?.status === 404) return NO_CAPTIONS
	if (err?.status === 400) return "This session's time range can't be exported."
	return 'Could not download captions. Try again.'
}

/** Saves a blob through a temporary link. */
export function saveBlob(blob, filename) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}
